import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { useLang } from '../lib/lang';

interface Props { user: AuthUser }

interface LibItem {
  id: string; catalogue_id: string; isbn: string | null;
  title_en: string; title_ms: string | null; author: string | null;
  publisher: string | null; publication_year: number | null;
  category: string; language: string; location_code: string | null;
  total_copies: number; available_copies: number;
  cover_image_url: string | null; school_name: string | null;
}

const BN = {
  pageTitle: 'Katalog Perpustakaan',
  pageSubtitle: 'tajuk',
  backLink: '← Laman Utama',
  addBtn: '+ Tambah Bahan',
  addFormTitle: 'Tambah Bahan Baharu',
  labelCatalogueId: 'ID Katalog *',
  labelIsbn: 'ISBN',
  labelTitleEn: 'Tajuk (EN) *',
  labelTitleMs: 'Tajuk (MS)',
  labelAuthor: 'Pengarang',
  labelPublisher: 'Penerbit',
  labelYear: 'Tahun',
  labelCategory: 'Kategori *',
  labelLanguage: 'Bahasa *',
  labelLocation: 'Lokasi',
  labelTotalCopies: 'Jml Salinan *',
  saveBtn: 'Simpan',
  savingBtn: 'Menambah…',
  cancelBtn: 'Batal',
  searchLabel: 'Cari',
  searchPlaceholder: 'Tajuk, pengarang, ID, ISBN…',
  filterCategoryLabel: 'Kategori',
  filterLanguageLabel: 'Bahasa',
  filterAll: 'Semua',
  availableOnly: 'Tersedia sahaja',
  searchBtn: 'Cari',
  retryBtn: 'Cuba Lagi',
  emptyTitle: 'Tiada Bahan Dijumpai',
  emptySubtitleAdmin: 'Tiada bahan dijumpai. Tambah bahan pertama di atas.',
  emptySubtitle: 'Tiada bahan dijumpai.',
  copiesLabel: 'salinan',
  availableBadge: 'tersedia',
  outOfStockBadge: 'Habis',
};

const EN = {
  pageTitle: 'Library Catalogue',
  pageSubtitle: 'titles',
  backLink: '← Home',
  addBtn: '+ Add New Item',
  addFormTitle: 'Add New Item',
  labelCatalogueId: 'Catalogue ID *',
  labelIsbn: 'ISBN',
  labelTitleEn: 'Title EN *',
  labelTitleMs: 'Title MS',
  labelAuthor: 'Author',
  labelPublisher: 'Publisher',
  labelYear: 'Year',
  labelCategory: 'Category *',
  labelLanguage: 'Language *',
  labelLocation: 'Location',
  labelTotalCopies: 'Total Copies *',
  saveBtn: 'Save',
  savingBtn: 'Adding…',
  cancelBtn: 'Cancel',
  searchLabel: 'Search',
  searchPlaceholder: 'Title, author, ID, ISBN…',
  filterCategoryLabel: 'Category',
  filterLanguageLabel: 'Language',
  filterAll: 'All',
  availableOnly: 'Available only',
  searchBtn: 'Search',
  retryBtn: 'Retry',
  emptyTitle: 'No Items Found',
  emptySubtitleAdmin: 'No items found. Add the first item above.',
  emptySubtitle: 'No items found.',
  copiesLabel: 'copies',
  availableBadge: 'available',
  outOfStockBadge: 'Out of stock',
};

const CATEGORIES = ['TEXTBOOK','REFERENCE','FICTION','NON_FICTION','MAGAZINE','AV','RELIGIOUS','OTHER'];
const CAT_LABELS: Record<string, string> = {
  TEXTBOOK: 'Teks', REFERENCE: 'Rujukan', FICTION: 'Fiksyen', NON_FICTION: 'Bukan Fiksyen',
  MAGAZINE: 'Majalah', AV: 'A/V', RELIGIOUS: 'Agama', OTHER: 'Lain-lain',
};
const CAT_COLORS: Record<string, string> = {
  TEXTBOOK: '#60a5fa', REFERENCE: '#a78bfa', FICTION: '#f472b6', NON_FICTION: '#4ade80',
  MAGAZINE: '#facc15', AV: '#fb923c', RELIGIOUS: '#34d399', OTHER: '#9ca3af',
};
const LANGUAGES = ['MALAY','ENGLISH','ARABIC','MANDARIN','OTHER'];
const LANG_LABELS: Record<string, string> = {
  MALAY: 'Melayu', ENGLISH: 'Inggeris', ARABIC: 'Arab', MANDARIN: 'Mandarin', OTHER: 'Lain',
};

const DEMO_SCHOOL = '3f868b12-b554-4f51-a893-69e9b4ba9f57';

function getSchoolId(user: AuthUser): string {
  return user.roles.find((r) => r.schoolId)?.schoolId ?? DEMO_SCHOOL;
}

export function LibraryCataloguePage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;

  const schoolId = getSchoolId(user);

  const [items,    setItems]    = useState<LibItem[]>([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [toast,    setToast]    = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [filterCat,   setFilterCat]   = useState('');
  const [filterLang,  setFilterLang]  = useState('');
  const [filterAvail, setFilterAvail] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm,     setAddForm]     = useState({
    catalogueId: '', titleEn: '', titleMs: '', author: '',
    publisher: '', isbn: '', publicationYear: '',
    category: 'TEXTBOOK', language: 'MALAY', locationCode: '',
    totalCopies: '1', barcode: '',
  });
  const [addError,  setAddError]  = useState<string | null>(null);
  const [adding,    setAdding]    = useState(false);

  const isAdmin = user.roles.some((r) => ['admin', 'principal', 'clerk', 'super_admin'].includes(r.role));

  const load = useCallback(async (p: number) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ schoolId, page: String(p) });
      if (searchInput.trim()) params.set('q', searchInput.trim());
      if (filterCat)          params.set('category', filterCat);
      if (filterLang)         params.set('language', filterLang);
      if (filterAvail)        params.set('availableOnly', 'true');
      const result = await apiGet<{ data: LibItem[]; total: number; page: number }>(`/api/library/items?${params}`);
      setItems(result.data);
      setTotal(result.total);
      setPage(result.page);
    } catch (err) {
      setError(err instanceof TypeError && err.message.includes('fetch')
        ? 'Tidak dapat menghubungi pelayan. / Cannot reach server.'
        : err instanceof Error ? err.message : 'Ralat tidak diketahui.');
    } finally { setLoading(false); }
  }, [schoolId, searchInput, filterCat, filterLang, filterAvail]);

  useEffect(() => { void load(1); }, [load]);

  function validateAddForm(): string | null {
    if (!addForm.catalogueId.trim()) return 'ID katalog diperlukan. / Catalogue ID required.';
    if (!addForm.titleEn.trim())     return 'Tajuk (EN) diperlukan. / Title (EN) required.';
    const copies = parseInt(addForm.totalCopies);
    if (isNaN(copies) || copies < 1 || copies > 9999) return 'Jumlah salinan mesti 1–9999. / Total copies must be 1–9999.';
    if (addForm.publicationYear) {
      const yr = parseInt(addForm.publicationYear);
      if (isNaN(yr) || yr < 1800 || yr > new Date().getFullYear() + 1)
        return `Tahun tidak sah (1800–${new Date().getFullYear()+1}). / Invalid year.`;
    }
    return null;
  }

  async function handleAdd() {
    const err = validateAddForm();
    if (err) { setAddError(err); return; }
    setAdding(true); setAddError(null);
    try {
      await apiPost('/api/library/items', {
        schoolId, catalogueId: addForm.catalogueId.trim().toUpperCase(),
        titleEn: addForm.titleEn.trim(), titleMs: addForm.titleMs.trim() || undefined,
        author: addForm.author.trim() || undefined, publisher: addForm.publisher.trim() || undefined,
        isbn: addForm.isbn.trim() || undefined, barcode: addForm.barcode.trim() || undefined,
        publicationYear: addForm.publicationYear ? parseInt(addForm.publicationYear) : undefined,
        category: addForm.category, language: addForm.language,
        locationCode: addForm.locationCode.trim() || undefined,
        totalCopies: parseInt(addForm.totalCopies),
      });
      setToast({ variant: 'success', message: 'Bahan berjaya ditambah ke katalog. / Item added to catalogue.' });
      setShowAddForm(false);
      setAddForm({ catalogueId:'', titleEn:'', titleMs:'', author:'', publisher:'', isbn:'', publicationYear:'', category:'TEXTBOOK', language:'MALAY', locationCode:'', totalCopies:'1', barcode:'' });
      void load(1);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Gagal menambah. / Failed to add item.');
    } finally { setAdding(false); }
  }

  const totalPages = Math.ceil(total / 20) || 1;

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>

          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>{UI.backLink}</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                📚 {UI.pageTitle}
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {total} {UI.pageSubtitle}
              </p>
            </div>
            {isAdmin && !showAddForm && (
              <Button variant="primary" size="sm" onClick={() => { setShowAddForm(true); setAddError(null); }}>
                {UI.addBtn}
              </Button>
            )}
          </div>

          {/* Add form */}
          {showAddForm && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg, boxShadow: shadows.xl }}>
              <p style={{ margin: `0 0 ${spacing.lg}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                {UI.addFormTitle}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <Field label={UI.labelCatalogueId} style={{ flex: '1 1 120px' }}>
                    <input type="text" value={addForm.catalogueId} onChange={(e) => setAddForm((f) => ({ ...f, catalogueId: e.target.value }))} disabled={adding} style={inputSt()} placeholder="cth: LIB-SMK01-001" maxLength={50} />
                  </Field>
                  <Field label={UI.labelIsbn} style={{ flex: '1 1 140px' }}>
                    <input type="text" value={addForm.isbn} onChange={(e) => setAddForm((f) => ({ ...f, isbn: e.target.value }))} disabled={adding} style={inputSt()} maxLength={20} />
                  </Field>
                  <Field label="Barcode" style={{ flex: '1 1 120px' }}>
                    <input type="text" value={addForm.barcode} onChange={(e) => setAddForm((f) => ({ ...f, barcode: e.target.value }))} disabled={adding} style={inputSt()} maxLength={100} />
                  </Field>
                </div>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <Field label={UI.labelTitleEn} style={{ flex: '2 1 200px' }}>
                    <input type="text" value={addForm.titleEn} onChange={(e) => setAddForm((f) => ({ ...f, titleEn: e.target.value }))} disabled={adding} style={inputSt()} maxLength={500} />
                  </Field>
                  <Field label={UI.labelTitleMs} style={{ flex: '2 1 200px' }}>
                    <input type="text" value={addForm.titleMs} onChange={(e) => setAddForm((f) => ({ ...f, titleMs: e.target.value }))} disabled={adding} style={inputSt()} maxLength={500} />
                  </Field>
                </div>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <Field label={UI.labelAuthor} style={{ flex: '2 1 160px' }}>
                    <input type="text" value={addForm.author} onChange={(e) => setAddForm((f) => ({ ...f, author: e.target.value }))} disabled={adding} style={inputSt()} maxLength={300} />
                  </Field>
                  <Field label={UI.labelPublisher} style={{ flex: '2 1 160px' }}>
                    <input type="text" value={addForm.publisher} onChange={(e) => setAddForm((f) => ({ ...f, publisher: e.target.value }))} disabled={adding} style={inputSt()} maxLength={300} />
                  </Field>
                  <Field label={UI.labelYear} style={{ flex: '1 1 80px' }}>
                    <input type="number" value={addForm.publicationYear} onChange={(e) => setAddForm((f) => ({ ...f, publicationYear: e.target.value }))} disabled={adding} style={inputSt()} min={1800} max={2100} />
                  </Field>
                </div>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <Field label={UI.labelCategory} style={{ flex: '1 1 120px' }}>
                    <select value={addForm.category} onChange={(e) => setAddForm((f) => ({ ...f, category: e.target.value }))} disabled={adding} style={inputSt()}>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{CAT_LABELS[c] ?? c}</option>)}
                    </select>
                  </Field>
                  <Field label={UI.labelLanguage} style={{ flex: '1 1 120px' }}>
                    <select value={addForm.language} onChange={(e) => setAddForm((f) => ({ ...f, language: e.target.value }))} disabled={adding} style={inputSt()}>
                      {LANGUAGES.map((l) => <option key={l} value={l}>{LANG_LABELS[l] ?? l}</option>)}
                    </select>
                  </Field>
                  <Field label={UI.labelLocation} style={{ flex: '1 1 100px' }}>
                    <input type="text" value={addForm.locationCode} onChange={(e) => setAddForm((f) => ({ ...f, locationCode: e.target.value }))} disabled={adding} style={inputSt()} maxLength={50} />
                  </Field>
                  <Field label={UI.labelTotalCopies} style={{ flex: '1 1 80px' }}>
                    <input type="number" value={addForm.totalCopies} onChange={(e) => { const v = parseInt(e.target.value); if (!isNaN(v) && v >= 1 && v <= 9999) setAddForm((f) => ({ ...f, totalCopies: e.target.value })); }} disabled={adding} style={inputSt()} min={1} max={9999} />
                  </Field>
                </div>
              </div>
              {addError && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{addError}</p>}
              <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
                <Button variant="primary" size="sm" onClick={() => void handleAdd()} disabled={adding} loading={adding}>
                  {adding ? UI.savingBtn : UI.saveBtn}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddForm(false); setAddError(null); }} disabled={adding}>{UI.cancelBtn}</Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.lg }}>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '3 1 200px' }}>
                <label style={labelSt}>{UI.searchLabel}</label>
                <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void load(1)} placeholder={UI.searchPlaceholder} disabled={loading} style={inputSt()} />
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <label style={labelSt}>{UI.filterCategoryLabel}</label>
                <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} disabled={loading} style={inputSt()}>
                  <option value="">{UI.filterAll}</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 100px' }}>
                <label style={labelSt}>{UI.filterLanguageLabel}</label>
                <select value={filterLang} onChange={(e) => setFilterLang(e.target.value)} disabled={loading} style={inputSt()}>
                  <option value="">{UI.filterAll}</option>
                  {LANGUAGES.map((l) => <option key={l} value={l}>{LANG_LABELS[l]}</option>)}
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, cursor: 'pointer', fontSize: typography.fontSize.sm, color: colors.neutral.white, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={filterAvail} onChange={(e) => setFilterAvail(e.target.checked)} />
                {UI.availableOnly}
              </label>
              <div style={{ display: 'flex', gap: spacing.xs }}>
                <Button variant="primary" size="sm" onClick={() => void load(1)} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : UI.searchBtn}
                </Button>
                {(searchInput || filterCat || filterLang || filterAvail) && (
                  <Button variant="ghost" size="sm" disabled={loading} onClick={() => { setSearchInput(''); setFilterCat(''); setFilterLang(''); setFilterAvail(false); }}>✕</Button>
                )}
              </div>
            </div>
          </div>

          {/* Error */}
          {error && <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
            <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{error}</p>
            <Button variant="ghost" size="sm" onClick={() => void load(page)} style={{ marginTop: spacing.xs }}>{UI.retryBtn}</Button>
          </div>}

          {loading && items.length === 0 && <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>}

          {!loading && !error && items.length === 0 && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: spacing.sm }}>📚</div>
              <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{UI.emptyTitle}</p>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {isAdmin ? UI.emptySubtitleAdmin : UI.emptySubtitle}
              </p>
            </div>
          )}

          {/* Grid */}
          {items.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: spacing.md, opacity: loading ? 0.7 : 1 }}>
              {items.map((item) => (
                <div key={item.id} style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, overflow: 'hidden', boxShadow: shadows.lg, display: 'flex', flexDirection: 'column' }}>
                  {/* Cover */}
                  <div style={{ height: '160px', backgroundColor: `${CAT_COLORS[item.category] ?? '#9ca3af'}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                    {item.cover_image_url
                      ? <img src={item.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      : <span style={{ fontSize: '40px' }}>📖</span>
                    }
                    {/* Availability badge */}
                    <div style={{ position: 'absolute', top: spacing.xs, right: spacing.xs }}>
                      <span style={{
                        fontSize: '10px', fontWeight: typography.fontWeight.bold,
                        color: item.available_copies > 0 ? '#4ade80' : '#f87171',
                        backgroundColor: item.available_copies > 0 ? 'rgba(74,222,128,0.15)' : 'rgba(248,113,113,0.15)',
                        padding: '2px 6px', borderRadius: borderRadius.full,
                      }}>
                        {item.available_copies > 0 ? `${item.available_copies} ${UI.availableBadge}` : UI.outOfStockBadge}
                      </span>
                    </div>
                    {/* Category pill */}
                    <div style={{ position: 'absolute', bottom: spacing.xs, left: spacing.xs }}>
                      <span style={{ fontSize: '10px', color: CAT_COLORS[item.category] ?? '#9ca3af', backgroundColor: `${CAT_COLORS[item.category] ?? '#9ca3af'}22`, padding: '1px 6px', borderRadius: borderRadius.full, fontWeight: typography.fontWeight.bold }}>
                        {CAT_LABELS[item.category] ?? item.category}
                      </span>
                    </div>
                  </div>

                  {/* Info */}
                  <div style={{ padding: spacing.sm, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <p style={{ margin: 0, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {item.title_en}
                    </p>
                    {item.author && <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.author}</p>}
                    <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                      {item.catalogue_id}
                      {item.location_code ? ` · ${item.location_code}` : ''}
                      {item.publication_year ? ` · ${item.publication_year}` : ''}
                    </p>
                    <div style={{ marginTop: 'auto', paddingTop: spacing.xs, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                        {item.total_copies} {UI.copiesLabel} · {LANG_LABELS[item.language] ?? item.language}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xl }}>
              <Button variant="ghost" size="sm" disabled={page <= 1 || loading} onClick={() => void load(page - 1)}>← {lang === 'en' ? 'Previous' : 'Sebelum'}</Button>
              <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages || loading} onClick={() => void load(page + 1)}>{lang === 'en' ? 'Next' : 'Seterusnya'} →</Button>
            </div>
          )}
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={style}><label style={labelSt}>{label}</label>{children}</div>;
}
const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return {
    width: '100%', backgroundColor: colors.neutral.black,
    border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md,
    color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.fontSize.sm, outline: 'none',
    boxSizing: 'border-box', fontFamily: typography.fontFamily.body,
  };
}
